import { z } from 'zod';

// =============================================================================
// Recording-URL allowlist
// =============================================================================
//
// The `interaction.recordingUrl` field is plumbed straight into a DOM
// `<audio src>` by the post-call drawer. We refuse to deserialize a
// row whose URL doesn't parse to one of the known storage hosts —
// defense in depth so a malformed row (data integrity bug, retired
// vendor, future SQL-injection bypass) can't drive client audio
// playback to an attacker-controlled origin. See PR review #5.

const ALLOWED_RECORDING_HOSTS: ReadonlyArray<string> = [
  'storage.googleapis.com', // GCS direct
  'storage.cloud.google.com', // GCS web console signed URL form
];

export function isAllowedRecordingOrigin(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    return ALLOWED_RECORDING_HOSTS.some(
      (host) => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`)
    );
  } catch {
    return false;
  }
}

// =============================================================================
// Enums & Constants
// =============================================================================

export const interactionChannelSchema = z.enum(['voice', 'sms', 'portal', 'email']);
export type InteractionChannel = z.infer<typeof interactionChannelSchema>;

export const interactionDirectionSchema = z.enum(['inbound', 'outbound']);
export type InteractionDirection = z.infer<typeof interactionDirectionSchema>;

export const interactionStatusSchema = z.enum([
  'pending',
  'in_progress',
  'completed',
  'failed',
  'transferred',
]);
export type InteractionStatus = z.infer<typeof interactionStatusSchema>;

/**
 * Kind of entry in the conversation stream.
 * See docs/conversation-view.md.
 */
export const interactionMessageKindSchema = z.enum([
  'user_message',
  'agent_message',
  'tool_request',
  'tool_response',
  'handoff',
  'call_ended',
]);
export type InteractionMessageKind = z.infer<typeof interactionMessageKindSchema>;

/**
 * Why a voice call ended — the terminal `call_ended` marker's payload.
 *
 * The voice runtime's teardown reason (`hangUp(reason)` in
 * apps/livekit/src/agents/runtime/run.ts) is a low-level string; the agent
 * maps it to one of these SEMANTIC categories before recording the marker, so
 * the transcript renders a stable, human-meaningful end reason regardless of
 * how the underlying teardown was wired. A dangling transcript with no marker
 * was generating "why did the call just stop?" support tickets — the marker is
 * the always-present, explicit end-of-call row (see docs/conversation-view.md).
 */
export const callEndReasonSchema = z.enum([
  /** Remote party (patient, or the browser tester) left the room first. */
  'patient_hung_up',
  /** Agent called `end_call` after wrap-up — the normal, clean close. */
  'assistant_ended',
  /** Answering machine / voicemail path (AMD or mid-call handoff to VM). */
  'voicemail',
  /** Line not reachable (machine-unavailable / screener) — no message left. */
  'not_reachable',
  /** Routed to a human / emergency transfer. */
  'transferred',
  /** Hit the max session-duration cap. */
  'max_duration',
  /** Teardown from an unexpected runtime error (task/loop threw). */
  'error',
]);
export type CallEndReason = z.infer<typeof callEndReasonSchema>;

export const interactionTypeSchema = z.enum([
  'callback',
  'appointment_confirmation',
  'appointment_reminder',
  'reschedule',
  'post_visit_checkup',
  'medication_reminder',
  'recall_reminder',
  'lab_results',
  'survey',
  'general',
]);
export type InteractionType = z.infer<typeof interactionTypeSchema>;

export const interactionAssignedToTypeSchema = z.enum(['agent', 'team', 'user', 'unassigned']);
export type InteractionAssignedToType = z.infer<typeof interactionAssignedToTypeSchema>;

export const assignedToTypeSchema = z.enum(['user', 'team', 'agent']);
export type AssignedToType = z.infer<typeof assignedToTypeSchema>;

export const taskPrioritySchema = z.enum(['low', 'medium', 'high', 'urgent']);
export type TaskPriority = z.infer<typeof taskPrioritySchema>;

// Alias for backwards compatibility
export const interactionPrioritySchema = taskPrioritySchema;
export type InteractionPriority = TaskPriority;

export const taskStatusSchema = z.enum(['open', 'in_progress', 'completed', 'cancelled']);
export type TaskStatus = z.infer<typeof taskStatusSchema>;

export const ehrSyncStatusSchema = z.enum(['not_required', 'pending', 'synced', 'failed']);
export type EhrSyncStatus = z.infer<typeof ehrSyncStatusSchema>;

// =============================================================================
// Cost Breakdown
// =============================================================================

export const costBreakdownSchema = z.object({
  stt: z
    .object({
      provider: z.string(),
      minutes: z.number(),
      cost: z.number(),
    })
    .optional(),
  tts: z
    .object({
      provider: z.string(),
      characters: z.number(),
      cost: z.number(),
    })
    .optional(),
  llm: z
    .object({
      provider: z.string(),
      inputTokens: z.number(),
      outputTokens: z.number(),
      cost: z.number(),
    })
    .optional(),
  livekit: z
    .object({
      minutes: z.number(),
      cost: z.number(),
    })
    .optional(),
  sms: z
    .object({
      segments: z.number(),
      cost: z.number(),
    })
    .optional(),
});

export type CostBreakdown = z.infer<typeof costBreakdownSchema>;

// =============================================================================
// Structured Output
// =============================================================================

export const structuredOutputSchema = z.object({
  type: z.string(),
  data: z.record(z.string(), z.unknown()),
});

export type StructuredOutput = z.infer<typeof structuredOutputSchema>;

// =============================================================================
// Interaction Response
// =============================================================================

export const interactionResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  patientId: z.string().uuid().nullable(),
  appointmentId: z.string().uuid().nullable(),
  agentId: z.string().nullable(),
  agentName: z.string().nullable(),
  channel: interactionChannelSchema,
  direction: interactionDirectionSchema.nullable(),
  phoneNumber: z.string().nullable(),
  email: z.string().nullable(),
  status: interactionStatusSchema,
  startedAt: z.string().datetime().nullable(),
  answeredAt: z.string().datetime().nullable(),
  /**
   * When the FAR END picked up, observed on the SIP leg (ADR-339).
   *
   * NOT interchangeable with `answeredAt`, and `answeredAt` must never be
   * substituted for it. `answeredAt` is stamped when the voice runtime POSTs
   * `/start`, which on an outbound dial happens while the phone is still
   * ringing — 9.1s early on int `a61b4bf1`, up to 30.2s across 53 prod calls.
   * Read `answeredAt` as "the session took the call" and this as "the person
   * picked up".
   *
   * Subtracting `recordingStartedAtMs` gives the silent pre-pickup lead-in the
   * review UI labels (`ringLeadIn`). Null is the normal state — inbound calls
   * and browser tests never observe a SIP answer at all, a call that was never
   * picked up has none, and no row predating the column has one — so treat
   * null as "no lead-in to show", never as zero.
   *
   * `.optional()` as well as nullable, matching `cancellationFeeCents` below:
   * the field is absent from an older API's payload, and neither deploy order
   * should be a type error.
   */
  sipAnsweredAt: z.string().datetime().nullable().optional(),
  endedAt: z.string().datetime().nullable(),
  durationSeconds: z.number().nullable(),
  // Allowlist enforced at the response boundary so a stored row whose
  // origin doesn't match a known recording host (data-integrity bug,
  // backfill from a vendor that's been retired, attacker write through
  // a future bypass) can't reach the browser as an `<audio src>`. Today's
  // only writer is the LiveKit egress to GCS; if you add another host
  // (e.g. a CDN in front), extend `isAllowedRecordingOrigin` below.
  // See PR review #5 — defense in depth on top of the storage signing.
  recordingUrl: z
    .string()
    .nullable()
    .refine((v) => v === null || isAllowedRecordingOrigin(v), {
      message: 'recordingUrl must point at an allowed storage origin',
    }),
  recordingDurationSeconds: z.number().nullable(),
  /**
   * Unix ms of the recording's FIRST SAMPLE, written by the egress webhook.
   *
   * The anchor the transcript is aligned against during playback: a message's
   * position in the audio is `message.timestamp - recordingStartedAtMs`.
   * Deliberately NOT `startedAt`, which is a few seconds earlier by a margin
   * that varies per call (egress spin-up), and null on every call recorded
   * before this column was populated — consumers must treat null as "no
   * alignment available" rather than falling back to `startedAt`.
   */
  recordingStartedAtMs: z.number().nullable(),
  livekitRoomName: z.string().nullable(),
  livekitRoomSid: z.string().nullable(),
  totalCost: z.number().nullable(),
  costBreakdown: costBreakdownSchema.nullable(),
  summary: z.string().nullable(),
  structuredOutput: z.array(structuredOutputSchema).nullable(),
  /**
   * What the CALLER said the call was worth, 1 (poor) to 5 (excellent), asked
   * by the voice agent as the last question before the goodbye (ADR-200).
   *
   * NOT the evaluator's score — those live on the evaluation and are withheld
   * from practices because they are the AI's grade on its own work (ADR-154).
   * Null means no rating was recorded and must never render as a zero or a
   * "Not rated": nothing usable came back, or the call ended first.
   */
  callRating: z.number().nullable(),
  /**
   * The late-cancellation fee the caller accepted on this call, in cents, or
   * null (ADR-290). Derived per read from the appointment record
   * (`scheduled_appointments.cancelled_interaction_id`), not the evaluation, so
   * it is there before — and without — the post-call evaluator. Only the
   * single-interaction read carries it.
   */
  cancellationFeeCents: z.number().int().nullable().optional(),
  wasEscalated: z.number(),
  escalatedTo: z.string().uuid().nullable(),
  escalatedAt: z.string().datetime().nullable(),
  escalationReason: z.string().nullable(),
  flagged: z.number(),
  flaggedAt: z.string().datetime().nullable(),
  flaggedBy: z.string().uuid().nullable(),
  flagReason: z.string().nullable(),
  // Outbound scheduling & assignment
  assignedTo: z.string().uuid().nullable(),
  assignedToType: assignedToTypeSchema.nullable(),
  assignedAt: z.string().datetime().nullable(),
  fireAt: z.string().datetime().nullable(),
  attemptCount: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type InteractionResponse = z.infer<typeof interactionResponseSchema>;

// =============================================================================
// Interaction Message Response — discriminated by `kind`
// =============================================================================

const baseInteractionMessageFields = {
  id: z.string().uuid(),
  interactionId: z.string().uuid(),
  organizationId: z.string().uuid(),
  sequence: z.number().int(),
  timestamp: z.string().datetime(),
  createdAt: z.string().datetime(),
  /**
   * Which agent produced this row. Populated on agent_message,
   * tool_request, and handoff (= the incoming agent). Null on
   * user_message + tool_response.
   *
   * Stored as plain text keyed by the specialist registry name
   * (`"primary"`, `"scheduling"`, …). Not an FK — agents live in code.
   */
  agentId: z.string().nullable(),
};

export const userMessageSchema = z.object({
  ...baseInteractionMessageFields,
  kind: z.literal('user_message'),
  text: z.string(),
  toolName: z.null(),
  toolCallId: z.null(),
  toolStatus: z.null(),
  toolDurationMs: z.null(),
  agentId: z.null(),
  payload: z.record(z.string(), z.unknown()).default({}),
});

export const agentMessageSchema = z.object({
  ...baseInteractionMessageFields,
  kind: z.literal('agent_message'),
  text: z.string(),
  toolName: z.null(),
  toolCallId: z.null(),
  toolStatus: z.null(),
  toolDurationMs: z.null(),
  payload: z.record(z.string(), z.unknown()).default({}),
});

export const toolRequestMessageSchema = z.object({
  ...baseInteractionMessageFields,
  kind: z.literal('tool_request'),
  text: z.null(),
  toolName: z.string(),
  toolCallId: z.string(),
  toolStatus: z.null(),
  toolDurationMs: z.null(),
  payload: z.object({
    arguments: z.record(z.string(), z.unknown()),
  }),
});

export const toolResponseMessageSchema = z.object({
  ...baseInteractionMessageFields,
  kind: z.literal('tool_response'),
  text: z.null(),
  toolName: z.string().nullable(),
  toolCallId: z.string(),
  toolStatus: z.enum(['ok', 'error']),
  toolDurationMs: z.number().int(),
  agentId: z.null(),
  payload: z.union([z.object({ result: z.unknown() }), z.object({ error: z.string() })]),
});

/**
 * Control transferred between agents. `agentId` is the INCOMING agent
 * (same as `payload.toAgentId`) — lets callers filter by who took over
 * without a payload read.
 */
export const handoffMessageSchema = z.object({
  ...baseInteractionMessageFields,
  kind: z.literal('handoff'),
  text: z.null(),
  toolName: z.null(),
  toolCallId: z.null(),
  toolStatus: z.null(),
  toolDurationMs: z.null(),
  payload: z.object({
    fromAgentId: z.string().nullable(),
    toAgentId: z.string(),
    firstMessage: z.string().nullable(),
  }),
});

/**
 * Terminal marker: the call ended. Always the last row of a completed voice
 * transcript. `payload.reason` is the semantic {@link callEndReasonSchema}
 * category; the FE owns the display label so the string lives in one place.
 */
export const callEndedMessageSchema = z.object({
  ...baseInteractionMessageFields,
  kind: z.literal('call_ended'),
  text: z.null(),
  toolName: z.null(),
  toolCallId: z.null(),
  toolStatus: z.null(),
  toolDurationMs: z.null(),
  agentId: z.null(),
  payload: z.object({ reason: callEndReasonSchema }),
});

export const interactionMessageResponseSchema = z.discriminatedUnion('kind', [
  userMessageSchema,
  agentMessageSchema,
  toolRequestMessageSchema,
  toolResponseMessageSchema,
  handoffMessageSchema,
  callEndedMessageSchema,
]);

export type InteractionMessageResponse = z.infer<typeof interactionMessageResponseSchema>;
export type UserMessage = z.infer<typeof userMessageSchema>;
export type AgentMessage = z.infer<typeof agentMessageSchema>;
export type ToolRequestMessage = z.infer<typeof toolRequestMessageSchema>;
export type ToolResponseMessage = z.infer<typeof toolResponseMessageSchema>;
export type HandoffMessage = z.infer<typeof handoffMessageSchema>;
export type CallEndedMessage = z.infer<typeof callEndedMessageSchema>;

// =============================================================================
// Task Response
// =============================================================================

export const taskResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  patientId: z.string().uuid().nullable(),
  appointmentId: z.string().uuid().nullable(),
  taskTypeId: z.string().uuid().nullable().optional(),
  title: z.string(),
  description: z.string().nullable(),
  priority: taskPrioritySchema,
  status: taskStatusSchema,
  structuredData: z.record(z.string(), z.unknown()).nullable(),
  // Narrow to the assignee discriminator so consumers get exhaustive
  // typing on `task.assignedToType === 'team'` and friends.
  assignedToType: assignedToTypeSchema.nullable(),
  assignedToId: z.string().uuid().nullable(),
  currentChannel: z.string().nullable().optional(),
  attemptCount: z.number().optional(),
  lastOutcome: z.string().nullable().optional(),
  scheduledAt: z.string().datetime().nullable().optional(),
  lastAttemptAt: z.string().datetime().nullable().optional(),
  finalOutcome: z.string().nullable().optional(),
  collectedData: z.record(z.string(), z.unknown()).nullable().optional(),
  source: z.string().optional(),
  contextData: z.record(z.string(), z.unknown()).nullable().optional(),
  dueAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  readAt: z.string().datetime().nullable().optional(),
  starredAt: z.string().datetime().nullable().optional(),
  ehrSyncStatus: ehrSyncStatusSchema,
  ehrSyncedAt: z.string().datetime().nullable(),
  ehrSyncError: z.string().nullable(),
  ehrReferenceId: z.string().nullable(),
  objectId: z.string().uuid().nullable().optional(),
  objectType: z.string().nullable().optional(),
  body: z.string().nullable().optional(),
  // `labels` is a first-class column on the task (classification slugs), so it
  // belongs on the base response — both the inbox-search and by-id endpoints
  // return it. Keeping it here (not only on taskWithRelationsSchema) means a
  // deep-linked / bookmarked task fetched via getById carries its labels too,
  // which gate the Overview action drawers and classification badges.
  labels: z.array(z.string()).optional().default([]),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type TaskResponse = z.infer<typeof taskResponseSchema>;

// =============================================================================
// Task History Response
// =============================================================================

export const taskHistoryResponseSchema = z.object({
  id: z.string().uuid(),
  taskId: z.string().uuid(),
  organizationId: z.string().uuid(),
  action: z.string(),
  previousValue: z.record(z.string(), z.unknown()).nullable(),
  newValue: z.record(z.string(), z.unknown()).nullable(),
  comment: z.string().nullable(),
  changedBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
});

export type TaskHistoryResponse = z.infer<typeof taskHistoryResponseSchema>;

// =============================================================================
// Request Schemas
// =============================================================================

export const interactionSearchRequestSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
  channel: interactionChannelSchema.optional(),
  status: z.string().optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  searchTerm: z.string().optional(),
});

export type InteractionSearchRequest = z.infer<typeof interactionSearchRequestSchema>;

export const createInteractionRequestSchema = z.object({
  patientId: z.string().uuid().optional(),
  appointmentId: z.string().uuid().optional(),
  agentId: z.string().optional(),
  agentName: z.string().optional(),
  channel: interactionChannelSchema,
  direction: interactionDirectionSchema.optional(),
  phoneNumber: z.string().optional(),
  email: z.string().email().optional(),
  livekitRoomName: z.string().optional(),
  livekitRoomSid: z.string().optional(),
});

export type CreateInteractionRequest = z.infer<typeof createInteractionRequestSchema>;

export const completeInteractionRequestSchema = z.object({
  summary: z.string().optional(),
  recordingUrl: z.string().url().optional(),
  recordingDurationSeconds: z.number().int().optional(),
  totalCost: z.number().optional(),
  costBreakdown: costBreakdownSchema.optional(),
  structuredOutput: z.array(structuredOutputSchema).optional(),
  // No `callRating`: the caller's own 1-5 rating is written only by the voice
  // agent through POST /internal/interactions/:id/complete (ADR-196), whose
  // schema owns its range. This public route never stored it.
});

export type CompleteInteractionRequest = z.infer<typeof completeInteractionRequestSchema>;

export const escalateInteractionRequestSchema = z.object({
  escalatedTo: z.string().uuid().optional(),
  reason: z.string().optional(),
});

export type EscalateInteractionRequest = z.infer<typeof escalateInteractionRequestSchema>;

/** POST /api/interactions/:id/unflag. `unflagPatient` also clears the patient's flag. */
export const unflagInteractionRequestSchema = z.object({
  unflagPatient: z.boolean().optional(),
});

export type UnflagInteractionRequest = z.infer<typeof unflagInteractionRequestSchema>;

/**
 * Request body for POST /internal/interactions/:id/messages.
 * Discriminated by `kind` — each kind has its own required fields.
 * See docs/conversation-view.md.
 */
export const recordInteractionMessageRequestSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('user_message'),
    text: z.string().min(1),
    timestamp: z.string().datetime().optional(),
  }),
  z.object({
    kind: z.literal('agent_message'),
    text: z.string().min(1),
    /**
     * Which agent produced the utterance. Matches the registry name
     * (`"primary"`, `"scheduling"`, …). Optional for backward
     * compatibility; callers that don't populate it produce null rows.
     */
    agentId: z.string().min(1).optional(),
    timestamp: z.string().datetime().optional(),
  }),
  z.object({
    kind: z.literal('tool_request'),
    toolCallId: z.string().min(1),
    toolName: z.string().min(1),
    arguments: z.record(z.string(), z.unknown()),
    agentId: z.string().min(1).optional(),
    timestamp: z.string().datetime().optional(),
  }),
  z.object({
    kind: z.literal('tool_response'),
    toolCallId: z.string().min(1),
    toolName: z.string().min(1),
    status: z.enum(['ok', 'error']),
    durationMs: z.number().int().min(0),
    result: z.unknown().optional(),
    error: z.string().optional(),
    timestamp: z.string().datetime().optional(),
  }),
  z.object({
    kind: z.literal('handoff'),
    /** null if the INCOMING agent is the session's first one. */
    fromAgentId: z.string().min(1).nullable(),
    toAgentId: z.string().min(1),
    /** The literal line the incoming agent will speak on enter. */
    firstMessage: z.string().nullable(),
    timestamp: z.string().datetime().optional(),
  }),
  z.object({
    kind: z.literal('call_ended'),
    /** Semantic end-of-call category — see {@link callEndReasonSchema}. */
    reason: callEndReasonSchema,
    timestamp: z.string().datetime().optional(),
  }),
]);

export type RecordInteractionMessageRequest = z.infer<typeof recordInteractionMessageRequestSchema>;

export const taskSearchRequestSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
  status: taskStatusSchema.optional(),
  assignedTo: z.string().uuid().optional(),
  priority: taskPrioritySchema.optional(),
  ehrSyncStatus: ehrSyncStatusSchema.optional(),
  searchTerm: z.string().optional(),
});

export type TaskSearchRequest = z.infer<typeof taskSearchRequestSchema>;

export const createTaskRequestSchema = z
  .object({
    patientId: z.string().uuid().optional(),
    appointmentId: z.string().uuid().optional(),
    title: z.string().min(1),
    description: z.string().optional(),
    priority: taskPrioritySchema.default('medium'),
    structuredData: z.record(z.string(), z.unknown()).optional(),
    assigneeType: z.enum(['user', 'team']).optional(),
    assigneeId: z.string().uuid().optional(),
    dueAt: z.string().datetime().optional(),
    ehrSyncStatus: z.enum(['not_required', 'pending']).optional(),
  })
  .refine((v) => (v.assigneeType == null) === (v.assigneeId == null), {
    message: 'assigneeType and assigneeId must be set together',
    path: ['assigneeId'],
  });

/** What a caller sends: `priority` may be omitted and defaults to 'medium'. */
export type CreateTaskRequest = z.input<typeof createTaskRequestSchema>;

export const updateTaskRequestSchema = z.object({
  title: z.string().min(1).optional(),
  description: z.string().optional(),
  priority: taskPrioritySchema.optional(),
  structuredData: z.record(z.string(), z.unknown()).optional(),
  dueAt: z.string().datetime().nullable().optional(),
});

export type UpdateTaskRequest = z.infer<typeof updateTaskRequestSchema>;

export const changeTaskStatusRequestSchema = z.object({
  status: taskStatusSchema,
});

export type ChangeTaskStatusRequest = z.infer<typeof changeTaskStatusRequestSchema>;

export const assignTaskRequestSchema = z.object({
  assigneeType: z.enum(['user', 'team']),
  assigneeId: z.string().uuid(),
});

export type AssignTaskRequest = z.infer<typeof assignTaskRequestSchema>;

export const addTaskCommentRequestSchema = z.object({
  comment: z.string().min(1),
});

export type AddTaskCommentRequest = z.infer<typeof addTaskCommentRequestSchema>;

/**
 * Most ids `POST /api/tasks/bulk-complete` takes in one request. Page-bounded
 * selection in the UI stays well below it; higher batches defeat the per-row
 * best-effort semantic (long sequential loops, larger blast radius on a
 * misclick).
 */
export const TASK_BULK_COMPLETE_CAP = 100;

export const bulkCompleteTasksRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(TASK_BULK_COMPLETE_CAP),
});

export type BulkCompleteTasksRequest = z.infer<typeof bulkCompleteTasksRequestSchema>;

export const scheduleInteractionRequestSchema = z.object({
  patientId: z.string().uuid(),
  interactionType: interactionTypeSchema,
  channel: interactionChannelSchema,
  scheduledAt: z.string().datetime(),
  priority: interactionPrioritySchema,
  assignedToType: interactionAssignedToTypeSchema,
  title: z.string(),
  contextData: z.record(z.string(), z.unknown()).optional(),
  appointmentId: z.string().uuid().optional(),
});

export type ScheduleInteractionRequest = z.infer<typeof scheduleInteractionRequestSchema>;

// =============================================================================
// Outbound Interaction Request
// =============================================================================

export const createOutboundRequestSchema = z.object({
  patientId: z.string().uuid(),
  agentId: z.string().uuid(), // Agent defines prompt, rules, etc.
  phoneNumber: z.string(),
  fireAt: z.string().datetime(), // ISO datetime
  assignedTo: z.string().uuid().optional(), // Optional assignment
  assignedToType: assignedToTypeSchema.optional(), // 'user' | 'team' | 'agent'
  contextData: z.record(z.string(), z.unknown()).optional(), // Data for agent context
  appointmentId: z.string().uuid().optional(),
});

export type CreateOutboundRequest = z.infer<typeof createOutboundRequestSchema>;

export const processOutboundRequestSchema = z.object({
  limit: z.number().int().min(1).max(1000).optional(),
});

export type ProcessOutboundRequest = z.infer<typeof processOutboundRequestSchema>;

// =============================================================================
// Process Outbound Response
// =============================================================================

export const processOutboundResponseSchema = z.object({
  processed: z.number().int(),
  interactions: z.array(
    z.object({
      id: z.string().uuid(),
      patientId: z.string().uuid().nullable(),
      phoneNumber: z.string().nullable(),
      fireAt: z.string().datetime().nullable(),
    })
  ),
});

export type ProcessOutboundResponse = z.infer<typeof processOutboundResponseSchema>;

// =============================================================================
// Interaction List with Metadata (for Activity view)
// =============================================================================

export const patientSummarySchema = z.object({
  id: z.string().uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phone: z.string().nullable(),
});

export type PatientSummary = z.infer<typeof patientSummarySchema>;

export const taskSummarySchema = z.object({
  id: z.string().uuid(),
  status: taskStatusSchema,
  assignedToId: z.string().uuid().nullable(),
  assignedToType: assignedToTypeSchema.nullable(),
  priority: taskPrioritySchema,
  dueAt: z.string().datetime().nullable(),
});

export type TaskSummary = z.infer<typeof taskSummarySchema>;

export const interactionWithMetadataSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  patientId: z.string().uuid().nullable(),
  channel: interactionChannelSchema,
  direction: interactionDirectionSchema.nullable(),
  /**
   * Canonical disposition (ADR-112) — how the call RESOLVED, orthogonal to
   * `status`, which says where it got to in the pipeline. Null until the
   * call reaches a terminal state, and null for every row written before
   * the column started being populated.
   *
   * Deliberately `z.string()` and not the shared enum: the column is free
   * text, written by whichever channel produced the call, so a row carrying
   * a value this build doesn't recognise must still be a row rather than a
   * type error at the edge of the list.
   */
  outcomeCategory: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  status: interactionStatusSchema,
  startedAt: z.string().datetime().nullable(),
  endedAt: z.string().datetime().nullable(),
  durationSeconds: z.number().nullable(),
  summary: z.string().nullable(),
  wasEscalated: z.number(),
  escalationReason: z.string().nullable(),
  flagged: z.number(),
  agentName: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // Joined data
  patient: patientSummarySchema.nullable(),
  task: taskSummarySchema.nullable(),
});

export type InteractionWithMetadata = z.infer<typeof interactionWithMetadataSchema>;

export const interactionListRequestSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
  channel: interactionChannelSchema.optional(),
  status: interactionStatusSchema.optional(),
  direction: interactionDirectionSchema.optional(),
  outcomeCategory: z.string().optional(),
  /**
   * True to exclude calls that were queued but never placed. See the
   * `dialed` filter in `InteractionRepository.listWithMetadata` for why a
   * `startedAt` timestamp does not answer this.
   */
  dialed: z.boolean().optional(),
  hasTask: z.boolean().optional(),
  assignedTo: z.string().uuid().optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  search: z.string().optional(),
});

export type InteractionListRequest = z.infer<typeof interactionListRequestSchema>;

// =============================================================================
// Interaction Event (Timeline) Response
// =============================================================================

export const eventStatusSchema = z.union([z.literal(0), z.literal(1), z.literal(2)]);
export type EventStatusType = z.infer<typeof eventStatusSchema>;

export const interactionEventResponseSchema = z.object({
  id: z.string().uuid(),
  interactionId: z.string().uuid(),
  organizationId: z.string().uuid(),
  eventType: z.string(),
  title: z.string(),
  eventData: z.record(z.string(), z.unknown()).nullable(),
  objectType: z.string().nullable(),
  objectId: z.string().uuid().nullable(),
  status: eventStatusSchema,
  errorMessage: z.string().nullable(),
  occurredAt: z.string().datetime(),
  createdAt: z.string().datetime(),
});

export type InteractionEventResponse = z.infer<typeof interactionEventResponseSchema>;

// =============================================================================
// Task Inbox Search (enriched with email/document extraction data)
// =============================================================================

export const taskInboxSearchRequestSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
  status: z.union([taskStatusSchema, z.array(taskStatusSchema)]).optional(),
  priority: z.union([taskPrioritySchema, z.array(taskPrioritySchema)]).optional(),
  readStatus: z.enum(['read', 'unread', 'all']).optional(),
  starredStatus: z.enum(['starred', 'unstarred', 'all']).optional(),
  searchTerm: z.string().max(200).optional(),
  sortBy: z.enum(['createdAt', 'priority', 'status']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  assignedTo: z.string().uuid().optional(),
  /**
   * Disambiguator for `assignedTo` — when present, the API filters on
   * both `(assigned_to_id, assigned_to_type)`. UUIDs are unique enough
   * that id-alone matching works in practice, but the explicit type
   * pins intent (e.g. dashboard "tasks by team" drilldown) and is
   * forward-compatible if the assignee namespace ever expands.
   */
  assigneeType: z.enum(['user', 'team', 'agent']).optional(),
  /**
   * Match tasks assigned to the calling user OR to any team they're a
   * member of. Server-resolved against the session — overrides assignedTo
   * when set. Mutually exclusive with `unassigned` (clients should not set both).
   */
  mine: z.boolean().optional(),
  /**
   * Match tasks with `assigned_to_type = 'unassigned'` (no user, team, or
   * agent owns them yet). Mutually exclusive with `mine`.
   */
  unassigned: z.boolean().optional(),
  labels: z.array(z.string().max(100)).max(50).optional(),
  dateFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be ISO date format YYYY-MM-DD')
    .optional(),
  dateTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be ISO date format YYYY-MM-DD')
    .optional(),
});

export type TaskInboxSearchRequest = z.infer<typeof taskInboxSearchRequestSchema>;

// =============================================================================
// Task Inbox Counts (for the tab + pinned-badge UI)
// =============================================================================

/**
 * Request shape for `POST /api/tasks/counts` — same filter axes as
 * `taskInboxSearchRequestSchema` minus pagination/sort/search/labels
 * since the response is per-scope tab counts (one row across the three
 * tabs). The user-supplied label filter doesn't change the tab counts
 * (each tab shows the count regardless of label sub-filter).
 */
export const taskCountsRequestSchema = z.object({
  /** Status filter the user is currently looking at — counts respect it. */
  status: z.union([taskStatusSchema, z.array(taskStatusSchema)]).optional(),
  /** Date-range filter the user is currently looking at. */
  dateFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be ISO date format YYYY-MM-DD')
    .optional(),
  dateTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be ISO date format YYYY-MM-DD')
    .optional(),
  /**
   * Optional assignee filter. Mirrors the inbox-search request shape so
   * tab counts match the filtered list when the user has pinned an
   * assignee via URL (e.g. dashboard "tasks by assignee" drilldown).
   */
  assignedTo: z.string().uuid().optional(),
  /** Disambiguator for `assignedTo` — see `taskInboxSearchRequestSchema`. */
  assigneeType: z.enum(['user', 'team', 'agent']).optional(),
  /**
   * Optional labels filter. Without this, dashboard label drilldowns
   * filter the LIST correctly but the tab counts above still show the
   * org-wide totals — making the user think nothing happened.
   */
  labels: z.array(z.string().max(100)).max(50).optional(),
});

export type TaskCountsRequest = z.infer<typeof taskCountsRequestSchema>;

export const taskCountsResponseSchema = z.object({
  /**
   * Counts for each scope tab (Mine / All / Unassigned), computed under
   * the same status / date filters the client is showing.
   */
  scopes: z.object({
    mine: z.number().int().nonnegative(),
    all: z.number().int().nonnegative(),
    unassigned: z.number().int().nonnegative(),
  }),
});

export type TaskCountsResponse = z.infer<typeof taskCountsResponseSchema>;

export const taskWithRelationsSchema = taskResponseSchema.extend({
  // `labels` now lives on the base taskResponseSchema. The remaining fields are
  // join-derived enrichments the inbox-search endpoint computes per row
  // (patient name, assignee display name) that the by-id endpoint does not.
  patientName: z.string().nullable().optional(),
  assignedToName: z.string().nullable().optional(),
});

export type TaskWithRelations = z.infer<typeof taskWithRelationsSchema>;

/**
 * Reasons a row in `bulkComplete` can fail. Defined as a single source
 * of truth so the API, the client, the failure dialog, and any future
 * test fixtures cannot drift out of sync.
 *
 * `not_found` covers BOTH "id doesn't exist in caller's org" (caught by
 * the upfront batch SELECT) AND "row was deleted/archived between the
 * SELECT and the per-row UPDATE" (caught by the NotFoundError branch in
 * the loop). `error` is everything else — deadlock, timeout, schema
 * violation. The transient case is the one logged with the underlying
 * error so prod debugging can find what failed.
 */
export const BULK_COMPLETE_FAIL_REASONS = ['not_found', 'error'] as const;
export type BulkCompleteFailReason = (typeof BULK_COMPLETE_FAIL_REASONS)[number];

export const bulkCompleteResultSchema = z.object({
  completed: z.array(z.string()),
  failed: z.array(
    z.object({
      id: z.string(),
      reason: z.enum(BULK_COMPLETE_FAIL_REASONS),
    })
  ),
});

export type BulkCompleteResult = z.infer<typeof bulkCompleteResultSchema>;

// =============================================================================
// Task Overview — notes, timeline, related, summary
// =============================================================================

export const taskNoteSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  taskId: z.string().uuid(),
  userId: z.string().uuid(),
  body: z.string(),
  mentionedUserIds: z.array(z.string().uuid()),
  rowStatus: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TaskNote = z.infer<typeof taskNoteSchema>;

export const createTaskNoteRequestSchema = z.object({
  body: z.string().min(1).max(5000),
  mentionedUserIds: z.array(z.string().uuid()).default([]),
});
export type CreateTaskNoteRequest = z.infer<typeof createTaskNoteRequestSchema>;

export const taskSummaryRequestSchema = z.object({
  /** Bypass the cached summary and overwrite it. */
  regenerate: z.boolean().optional(),
});

export type TaskSummaryRequest = z.infer<typeof taskSummaryRequestSchema>;

/**
 * Discriminated union — `kind` selects the payload shape. Frontend
 * renders different components per kind. Sorted newest-first by the api.
 */
export const taskTimelineEventSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('interaction'),
    id: z.string(),
    timestamp: z.string(),
    payload: z.object({
      channel: z.string(),
      direction: z.string().nullable(),
      status: z.string(),
      durationSeconds: z.number().nullable(),
      summary: z.string().nullable(),
      agentName: z.string().nullable(),
    }),
  }),
  z.object({
    kind: z.literal('history'),
    id: z.string(),
    timestamp: z.string(),
    payload: z.object({
      action: z.string(),
      actorType: z.string(),
      actorName: z.string().nullable(),
      fromStatus: z.string().nullable(),
      toStatus: z.string().nullable(),
      comment: z.string().nullable(),
      reason: z.string().nullable(),
      /**
       * Human-readable, single-line description for the row body
       * ("Assigned to Brianna", "Rachel closed the task"). Composed
       * server-side so the frontend doesn't repeat the action-enum
       * switch and user-name lookups. `null` when the action is
       * unrecognized or the inputs are too sparse to summarize.
       */
      description: z.string().nullable(),
    }),
  }),
  z.object({
    kind: z.literal('note'),
    id: z.string(),
    timestamp: z.string(),
    payload: z.object({
      userId: z.string().uuid(),
      body: z.string(),
      mentionedUserIds: z.array(z.string().uuid()),
    }),
  }),
]);
export type TaskTimelineEvent = z.infer<typeof taskTimelineEventSchema>;

export const taskRelatedResponseSchema = z.object({
  childTasks: z.array(taskResponseSchema),
  otherOpenPatientTasks: z.array(taskResponseSchema),
});
export type TaskRelatedResponse = z.infer<typeof taskRelatedResponseSchema>;

/**
 * Timeline response — events plus a per-source truncation flag.
 *
 * The server caps each source at a fixed limit (interaction / history /
 * note) so a runaway task can't blow up the response. Without
 * `hasMoreByKind` the FE silently shows the first N rows and the user
 * doesn't know more exist — a real "where did my data go" footgun for
 * support cases. When any flag is true the FE renders a "showing first
 * N — load more" affordance.
 */
export const taskTimelineResponseSchema = z.object({
  events: z.array(taskTimelineEventSchema),
  hasMoreByKind: z.object({
    interaction: z.boolean(),
    history: z.boolean(),
    note: z.boolean(),
  }),
});
export type TaskTimelineResponse = z.infer<typeof taskTimelineResponseSchema>;

export const taskSummaryResponseSchema = z.object({
  summary: z.string(),
  generatedAt: z.string(),
  cached: z.boolean(),
});
export type TaskSummaryResponse = z.infer<typeof taskSummaryResponseSchema>;

// =============================================================================
// Live Calls
// =============================================================================

/**
 * How far a live call has got, in order. A response carries the 1-based
 * `index` of the key in this list plus the list length, so the UI draws a
 * progress bar without having to know the vocabulary.
 *
 * DERIVED, NOT REPORTED — and that is the whole caveat. Nothing in the voice
 * runtime declares a stage; the API infers one from the tool calls already
 * written to `interaction_messages`, which is the only per-call progress
 * signal that exists today. It is therefore a reading of what the agent has
 * DONE, never a statement of what it intends to do next, and a flow that
 * books without ever calling a tool we map will sit on its floor stage for
 * the whole call. See `apps/api/src/core/interactions/live-stage.ts`.
 *
 * `connected` is the floor for a call that has been answered but has not yet
 * called a tool we recognise. It exists so that state has an honest name:
 * the alternative was to report `identifying`, which is true of an outbound
 * referral call's opening seconds and false of an inbound caller who opens
 * by saying what they want.
 */
export const LIVE_CALL_STAGES = [
  'reaching',
  'connected',
  'identifying',
  'understanding',
  'offering',
  'confirming',
  'wrapping',
] as const;

export const liveCallStageSchema = z.enum(LIVE_CALL_STAGES);
export type LiveCallStage = z.infer<typeof liveCallStageSchema>;

/**
 * How much the row should shout. `urgent` is an escalation in flight,
 * `attention` a flagged or long-running call, `good` a call that has already
 * booked something.
 */
export const liveCallToneSchema = z.enum(['normal', 'good', 'attention', 'urgent']);
export type LiveCallTone = z.infer<typeof liveCallToneSchema>;

export const liveCallStageInfoSchema = z.object({
  key: liveCallStageSchema,
  /** Human-readable, server-owned so FE and API never drift on wording. */
  label: z.string(),
  /** 1-based position of `key` in LIVE_CALL_STAGES. */
  index: z.number().int().min(1),
  /** LIVE_CALL_STAGES.length — the denominator for a progress bar. */
  total: z.number().int().min(1),
});

export type LiveCallStageInfo = z.infer<typeof liveCallStageInfoSchema>;

export const liveCallLastMessageSchema = z.object({
  speaker: z.enum(['agent', 'caller']),
  text: z.string(),
  at: z.string().datetime(),
});

export type LiveCallLastMessage = z.infer<typeof liveCallLastMessageSchema>;

export const liveCallResponseSchema = z.object({
  id: z.string().uuid(),
  direction: interactionDirectionSchema.nullable(),
  /**
   * Why the call is happening — the `CallReason` the runtime actually ran
   * with (`interactions.call_intent`). Null on inbound calls that never
   * reported one and on rows predating the column.
   */
  reason: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  agentName: z.string().nullable(),
  /**
   * Whether anyone has picked up. False means the line is still ringing:
   * `claimForDial` moves a row to `in_progress` at DIAL time, and only
   * `startInternal` sets `answered_at` once the runtime is on the call. So
   * `in_progress` alone does not mean a conversation is happening.
   */
  connected: z.boolean(),
  startedAt: z.string().datetime().nullable(),
  answeredAt: z.string().datetime().nullable(),
  /** Seconds since pickup, or since the dial when still ringing. */
  elapsedSeconds: z.number().int().min(0),
  /** Which dial attempt this is. 0 on inbound. */
  attemptCount: z.number().int().min(0),
  flagged: z.boolean(),
  escalated: z.boolean(),
  /** An appointment is already attached to this call. */
  booked: z.boolean(),
  tone: liveCallToneSchema,
  stage: liveCallStageInfoSchema,
  patient: z
    .object({
      id: z.string().uuid(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      phone: z.string().nullable(),
    })
    .nullable(),
  lastMessage: liveCallLastMessageSchema.nullable(),
});

export type LiveCallResponse = z.infer<typeof liveCallResponseSchema>;

export const liveCallListResponseSchema = z.object({
  items: z.array(liveCallResponseSchema),
  /**
   * How many calls this response carries — NOT a count of every live call
   * that exists. The query is capped, so at implausible concurrency this
   * is the cap. It is the right number to render beside a list that shows
   * exactly these rows, and the wrong one to report as a metric.
   */
  total: z.number().int().min(0),
  inbound: z.number().int().min(0),
  outbound: z.number().int().min(0),
});

export type LiveCallListResponse = z.infer<typeof liveCallListResponseSchema>;

/**
 * How many voice calls are on the line right now. Unlike
 * `LiveCallListResponse.total`, this is NOT capped: it counts every live
 * call, for a badge that shows the number without the roster.
 */
export const liveCallCountResponseSchema = z.object({
  total: z.number().int().min(0),
});

export type LiveCallCountResponse = z.infer<typeof liveCallCountResponseSchema>;

/**
 * Connection details for listening to a call that is already in progress.
 *
 * The token this carries is subscribe-only and hidden — see
 * `apps/livekit/src/monitor.ts`. It cannot publish audio, so a supervisor
 * holding it cannot be heard by the patient, and it does not appear in the
 * room's participant list, so it cannot perturb the agent.
 *
 * Short-lived by design. It grants access to live patient audio; treat it
 * like a password and never log or persist it.
 */
export const liveCallListenResponseSchema = z.object({
  roomName: z.string(),
  token: z.string(),
  livekitUrl: z.string(),
});

export type LiveCallListenResponse = z.infer<typeof liveCallListenResponseSchema>;
